import React from 'react';
import { Box, Typography } from '@mui/material';
import useTranslate from '../../Hooks/useTranslate';
import { resolveIcon } from '../../Utils/constants/icons';
import { useFieldValue } from './useFieldValue';
import ReadOnlyValue from './ReadOnlyValue';

/**
 * One of a declared set of answers, as a row of buttons.
 *
 * The choices are the field's `options` - value, wording, and an `icon` and `color` for each - so
 * how many there are and what they stand for is the descriptor's, not this component's. A choice
 * whose value is nothing is the one that applies no filter.
 *
 * @param {Object} props - Component props.
 * @param {String} props.basePath - Where in the form store the value lives.
 * @param {Object} props.field - The compiled field, carrying its options.
 *
 * @returns {React.JSX.Element}
 */
export const SegmentedField = function SegmentedField({ basePath, field }) {
  const translate = useTranslate();
  const [value, setValue] = useFieldValue(basePath, field);

  const choices = field.options ?? [];
  const chosen = choices.find(
    (choice) => (choice.value ?? null) === (value ?? null)
  );

  if (field.readOnly)
    return (
      <ReadOnlyValue
        id={field.key}
        label={field.label}
        value={translate(chosen?.label ?? '')}
      />
    );

  return (
    // The same margins every other field carries, so a segmented control lines up with the selects
    // and text fields beside it in a row rather than riding above them.
    <Box sx={{ px: '4px', mt: 2, mb: 1 }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: 'text.secondary',
          mb: '10px',
        }}
      >
        {translate(field.label)}
      </Typography>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        {choices.map((choice) => {
          const active = chosen === choice;
          const Icon = choice.icon ? resolveIcon(choice.icon) : null;

          return (
            <Box
              key={String(choice.value)}
              id={`${field.key}-${String(choice.value)}`}
              role="button"
              onClick={() => setValue(choice.value ?? null)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '5px',
                py: '11px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                color: active ? 'primary.main' : 'unset',
                border: active ? '2px solid' : '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
              }}
            >
              {Icon && <Icon sx={{ fontSize: 22, color: choice.color }} />}
              {translate(choice.label)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SegmentedField;
